import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { conciergeAPI } from 'api/concierge';
import type { ITestSession } from 'api/concierge';
import { setTestSession, show } from 'store/conciergeSlice';
import toast from 'app/toast';
import useBundleTranslation from 'i18n';

export interface IOpenTestSessionParams {
    /** A semantic model (report editor) or a domain (Domain Editor): exactly one of the two. */
    modelObjectUid?: string;
    conciergeDomainId?: number;
    elementId?: number;
    sessionLabel?: string;
}

export interface IOpenTestSessionOptions {
    /** Checked once the request resolves; a false answer drops the session instead of applying it. */
    apply?: () => boolean;
}

// The scope a running test session was opened with, so a new chat inside it re-opens the same
// model (report editor) or domain (Domain Editor preview) instead of dropping to an ordinary chat.
export function testSessionScopeParams(session: ITestSession): IOpenTestSessionParams {
    if ('domain' === session.scope_kind && session.concierge_domain_id) {
        return { conciergeDomainId: session.concierge_domain_id };
    }
    return { modelObjectUid: session.model_object_uid, elementId: session.element_id };
}

export function useOpenTestSession() {
    const dispatch = useDispatch();
    const { t } = useBundleTranslation(['app/concierge/widget']);

    return useCallback(
        async (
            { modelObjectUid, conciergeDomainId, elementId, sessionLabel }: IOpenTestSessionParams,
            options: IOpenTestSessionOptions = {},
        ): Promise<boolean> => {
            try {
                const session = await conciergeAPI.createTestSession({
                    model_object_uid: modelObjectUid,
                    concierge_domain_id: conciergeDomainId,
                    element_id: elementId,
                    session_label: sessionLabel,
                });
                if (options.apply && !options.apply()) return false;
                dispatch(setTestSession(session));
                dispatch(show());
                return true;
            } catch (error: any) {
                toast('error', error?.response?.data?.message ?? t('test_session.open_failed'));
                return false;
            }
        },
        [dispatch, t],
    );
}
